"use client";

import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

import { cloneWork } from "@/actions/work";
import type { WorkModel } from "@/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { queryKeys } from "@/constants/query";

const CloneWorkFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  cloneWorkData: z.boolean().default(true),
  cloneChildren: z.boolean().default(false),
  cloneAssignments: z.boolean().default(false),
  cloneAsAProject: z.boolean().default(false),
  cloneEstimates: z.boolean().default(false),
});

type CloneWorkFormValues = z.infer<typeof CloneWorkFormSchema>;

interface CloneWorkFormProps {
  workItem: WorkModel | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CloneWorkForm({
  workItem,
  open,
  onOpenChange,
  onSuccess,
}: CloneWorkFormProps) {
  const [loading, setLoading] = useState(false);
  const params = useParams();
  const orgId = params?.orgId as string;
  const projectId = params?.projectId as string;
  const queryClient = useQueryClient();

  // Use Zod input/output types to align with resolver signature
  // - TFieldValues (input): z.input<typeof CloneWorkFormSchema> (allows undefineds due to defaults)
  // - TTransformedValues (output): CloneWorkFormValues (all required after defaults)
  const form = useForm<z.input<typeof CloneWorkFormSchema>, unknown, CloneWorkFormValues>({
    resolver: zodResolver(CloneWorkFormSchema),
    defaultValues: {
      name: workItem ? `Clone of ${workItem.name || "Unnamed"}` : "",
      cloneWorkData: true,
      cloneChildren: false,
      cloneAssignments: false,
      cloneAsAProject: false,
      cloneEstimates: false,
    },
  });

  // Reset form when workItem changes or modal opens
  useEffect(() => {
    if (workItem && open) {
      form.reset({
        name: `Clone of ${workItem.name || "Unnamed"}`,
        cloneWorkData: true,
        cloneChildren: false,
        cloneAssignments: false,
        cloneAsAProject: false,
        cloneEstimates: false,
      });
    }
  }, [workItem, open, form]);

  const onSubmit = async (values: CloneWorkFormValues) => {
    if (!workItem?.id) {
      toast.error("No work item selected for cloning");
      return;
    }

    try {
      setLoading(true);

      // Clone the work item with specified options
      await cloneWork(workItem.id, {
        name: values.name,
        cloneWorkData: values.cloneWorkData,
        cloneChildren: values.cloneChildren,
        cloneAssignments: values.cloneAssignments,
        cloneAsAProject: values.cloneAsAProject,
        cloneEstimates: values.cloneEstimates,
      });

      // Build success message based on what was cloned
      const clonedItems = [];
      if (values.cloneChildren) clonedItems.push("children");
      if (values.cloneAssignments) clonedItems.push("assignments");
      if (values.cloneEstimates) clonedItems.push("estimates");

      const itemType = values.cloneAsAProject ? "Project" : (workItem.parentId ? "Work item" : "Project");
      const successMessage = `${itemType} cloned successfully${
        clonedItems.length > 0 ? ` with ${clonedItems.join(", ")}` : ""
      }`;

      toast.success(successMessage);

      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkTreeById(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectItems(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectNavItems(orgId),
      });

      // Close the modal and call success callback
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to clone work item:", error);
      toast.error("Failed to clone work item");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Clone Work Item</DialogTitle>
          <DialogDescription>
            Create a copy of "{workItem?.name}" with the selected options.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter clone name"
                      {...field}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

        <div className="space-y-4">
          <h3 className="text-sm font-medium">Clone Options</h3>

          <FormField
            control={form.control}
            name="cloneWorkData"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-lg border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={loading}
                  />
                </FormControl>
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Clone Work Data</FormLabel>
                  <FormDescription>
                    Clone the basic work item information (Dates, Budget, etc.)
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cloneChildren"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-lg border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={loading}
                  />
                </FormControl>
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Clone Children</FormLabel>
                  <FormDescription>
                    Also clone all child work items recursively
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cloneAssignments"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-lg border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={loading}
                  />
                </FormControl>
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Clone Assignments</FormLabel>
                  <FormDescription>
                    Clone the assigned people and agents
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cloneAsAProject"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-lg border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={loading}
                  />
                </FormControl>
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Clone as Project</FormLabel>
                  <FormDescription>
                    Clone this work item as a new project instead of a work item
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cloneEstimates"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-lg border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={loading}
                  />
                </FormControl>
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Clone Estimates</FormLabel>
                  <FormDescription>
                    Clone effort and cost estimates
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Cloning..." : "Clone"}
          </Button>
        </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
